import type {
  AccountRole,
  Alert,
  Benchmark,
  CoverageStat,
  DashboardSummary,
  DemandProfile,
  Invoice,
  MispricingRow,
  OpportunityFinding,
  PlanDefinition,
  PlanTier,
  PricingRecommendation,
  RestockFinding,
  ShopPerformance,
  Subscription,
  UndercutFinding,
  WebhookStatus,
} from '@dca/shared';

const TOKEN_KEY = 'dca.token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private browsing - the session simply does not persist */
  }
}

/**
 * A 402 means the endpoint exists but the current plan does not include it.
 * It is thrown as a typed error so a page can render an upgrade prompt instead
 * of an error state - the distinction matters, one is a sales moment and the
 * other is a bug.
 */
export class UpgradeRequiredError extends Error {
  constructor(
    readonly feature: string,
    readonly currentTier: PlanTier,
    readonly requiredTier: PlanTier,
  ) {
    super('Upgrade required');
    this.name = 'UpgradeRequiredError';
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Where the API lives.
 *
 * Empty in development and for a single-origin deployment, where the dev server
 * or a reverse proxy forwards `/api` to the backend. Set to the tunnel hostname
 * when the dashboard is served from Cloudflare Pages and the API is a different
 * origin - the two cannot share a relative path across hostnames.
 *
 * Trailing slashes are trimmed so `https://api.example.com/` and
 * `https://api.example.com` behave identically.
 */
const API_BASE = resolveApiBase(import.meta.env.VITE_API_BASE_URL);

/**
 * Set when VITE_API_BASE_URL held something unusable, so every request can say
 * so instead of failing further downstream.
 *
 * Falling back to same-origin is right when the variable is simply unset, and
 * wrong when it was set to a placeholder: the dashboard is static hosting whose
 * catch-all answers /api/v1/anything with index.html and a 200, so the app gets
 * HTML where JSON belongs and reports a parse error rather than a misconfigured
 * build. Naming the cause once here is worth more than the same puzzle on every
 * screen.
 */
let API_BASE_FAULT: string | null = null;

/**
 * Validates the configured API origin at startup.
 *
 * A placeholder like `https://api-chestr.<domain>` shipped once, baked into
 * the bundle, and every request died inside `fetch` with an unparseable URL -
 * including login, so the site looked broken with no way in. Vite inlines this
 * value at build time, so a bad one cannot be corrected without a rebuild;
 * catching it here at least names the cause instead of leaving a parse error.
 *
 * An unset value is fine and means same-origin, which is how local development
 * and a single-origin deployment both work.
 */
function resolveApiBase(raw: string | undefined): string {
  const value = (raw ?? '').trim().replace(/\/+$/, '');
  if (value === '') return '';

  try {
    const url = new URL(value);
    // A hostname carrying angle brackets or spaces is a template nobody filled
    // in. `new URL` accepts some of those, so check explicitly.
    if (/[<>\s]/.test(value) || !url.hostname.includes('.')) {
      throw new Error('placeholder');
    }
    return `${url.protocol}//${url.host}`;
  } catch {
    API_BASE_FAULT =
      `This build was compiled with VITE_API_BASE_URL set to "${value}", which is not a ` +
      'usable address, so it has no API to talk to. Set the variable to the API origin ' +
      'and redeploy - Vite bakes it in at build time, so it cannot be corrected without ' +
      'a rebuild.';
    // eslint-disable-next-line no-console
    console.error(API_BASE_FAULT);
    return '';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (API_BASE_FAULT) throw new ApiError(API_BASE_FAULT, 0);

  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1${path}`, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (response.status === 204) return undefined as T;

  // Static hosting answers an unknown path with the SPA shell and a 200, so a
  // request that never reached the API looks like a success until it is parsed.
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    throw new ApiError(
      `The API did not answer - ${API_BASE || 'this site'} returned a page rather than data. ` +
        'The dashboard is most likely built against the wrong API address.',
      response.status,
    );
  }

  const payload = await response.json().catch(() => ({}) as Record<string, unknown>);

  if (response.status === 402) {
    const body = payload as { feature?: string; currentTier?: PlanTier; requiredTier?: PlanTier };
    throw new UpgradeRequiredError(
      body.feature ?? 'unknown',
      body.currentTier ?? 'free',
      body.requiredTier ?? 'wood',
    );
  }

  if (!response.ok) {
    const body = payload as { error?: string };
    throw new ApiError(body.error ?? `Request failed (${response.status})`, response.status);
  }

  return payload as T;
}

const get = <T,>(path: string) => request<T>(path);
const post = <T,>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
const patch = <T,>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const del = <T,>(path: string) => request<T>(path, { method: 'DELETE' });

export interface Me {
  subscriber: {
    id: string;
    email: string;
    discordHandle: string | null;
    discordWebhookUrl: string | null;
    tier: PlanTier;
    role: AccountRole;
    status: string;
    isAdmin: boolean;
    timezoneOffset: number;
    createdAt: string;
  };
  plan: PlanDefinition;
  subscription: Subscription | null;
}

export interface Connection {
  id: string;
  accountType: 'personal' | 'firm';
  dcAccountCode: string;
  label: string | null;
  webhookStatus: WebhookStatus;
  shopEventsOnly: boolean;
  connectedAt: string | null;
  lastEventAt: string | null;
  eventCount: number;
  ingestUrl: string;
  shops: Array<{ id: string; name: string; city: string | null; district: string | null }>;
}

/** Low / average / high describe current listings; tradedAverage is separate. */
export interface PriceBandShape {
  low: number;
  high: number;
  average: number;
  tradedAverage: number | null;
  recommended: number;
  sampleSize: number;
  outliersRemoved: number;
  unitsPerDay: number | null;
  clearingUnits: number;
  tradeCount: number;
  recommendedReason: string;
}

export type SalesRangeKey = '24h' | 'week' | 'month' | 'all' | 'custom';

/** 'sell' is what the owner sold; 'buy' is what customers sold into the shop. */
export type TradeSide = 'sell' | 'buy';

/** Either a named window or an explicit pair of dates, never both. */
export type SalesWindow =
  | { range: Exclude<SalesRangeKey, 'custom'> }
  | { from: string; to: string };

function windowQuery(window: SalesWindow, side: TradeSide = 'sell'): string {
  const base =
    'range' in window
      ? `?range=${window.range}`
      : `?from=${encodeURIComponent(window.from)}&to=${encodeURIComponent(window.to)}`;
  return `${base}&side=${side}`;
}

export interface CreateConnectionResult {
  connection: Connection;
  webhookSecret: string;
  instructions: {
    noKeyNeeded: boolean;
    guideUrl: string;
    summary: string;
    steps: Array<{ title: string; detail: string; command?: string; link?: string }>;
    manageWebhook: string[];
    requirements: string[];
    fields: Array<{
      name: string;
      value: string | null;
      copyable: boolean;
      hint: string;
    }>;
  };
}

export interface TestPayload {
  url: string;
  headers: Record<string, string>;
  body: Record<string, unknown>;
  curl: string;
}

export const api = {
  auth: {
    login: (email: string, password: string) =>
      post<{ token: string }>('/auth/login', { email, password }),
    register: (
      email: string,
      password: string,
      role: AccountRole = 'buyer',
      discordHandle?: string,
    ) => post<{ token: string }>('/auth/register', { email, password, role, discordHandle }),
    me: () => get<Me>('/auth/me'),
    updateProfile: (body: {
      discordHandle?: string | null;
      discordWebhookUrl?: string | null;
      timezoneOffset?: number;
    }) => patch<{ subscriber: Me['subscriber'] }>('/auth/me', body),
  },

  onboarding: {
    connections: () =>
      get<{
        allowance: {
          personalUsed: number;
          personalLimit: number;
          firmsUsed: number;
          firmLimit: number;
          tier: string;
        };
        connections: Connection[];
      }>('/onboarding/connections'),
    instructions: (id: string) =>
      get<CreateConnectionResult>(`/onboarding/connections/${id}/instructions`),
    verifyStart: (ownerName: string) =>
      post<{
        ownerName: string;
        amount: string;
        payTo: string;
        status: string;
        expiresAt: string;
        command: string;
        note: string;
      }>('/onboarding/verify/start', { ownerName }),
    verifyStatus: (ownerName: string) =>
      get<{
        status: 'none' | 'pending' | 'verified' | 'expired' | 'cancelled';
        ownerName?: string;
        amount?: string;
        payTo?: string;
        paidBy?: string | null;
        hint?: string | null;
      }>(`/onboarding/verify/status?ownerName=${encodeURIComponent(ownerName)}`),
    lookup: (ownerName: string) =>
      get<{
        found: number;
        note: string;
        listings: Array<{
          shopId: number;
          material: string;
          itemName: string;
          unitPrice: number;
          batchQty: number;
          currentStock: number | null;
          world: string | null;
          coords: string | null;
        }>;
      }>(`/onboarding/lookup?ownerName=${encodeURIComponent(ownerName)}`),
    claim: (body: { ownerName?: string; firmId?: number; label?: string }) =>
      post<{
        connection: { id: string; dcAccountCode: string };
        shopsFound: number;
        worlds: string[];
        itemsListed: number;
        next: string;
      }>('/onboarding/claim', body),
    setSecret: (id: string, secret: string) =>
      post<{ ok: boolean; ingestUrl: string; next: string }>(
        `/onboarding/connections/${id}/secret`,
        { secret },
      ),
    create: (body: {
      accountType: 'personal' | 'firm';
      dcAccountCode: string;
      label?: string;
      privacyAcknowledged?: boolean;
    }) => post<CreateConnectionResult>('/onboarding/connections', body),
    status: (id: string) =>
      get<{
        webhookStatus: WebhookStatus;
        deliveries: number;
        eventCount: number;
        ingestUrl: string;
        nextStep: string;
      }>(`/onboarding/connections/${id}/status`),
    testPayload: (id: string) => get<TestPayload>(`/onboarding/connections/${id}/test-payload`),
    rotateSecret: (id: string) =>
      post<{ webhookSecret: string; ingestUrl: string }>(
        `/onboarding/connections/${id}/rotate-secret`,
      ),
    remove: (id: string) => del<{ ok: boolean }>(`/onboarding/connections/${id}`),
    addShop: (body: {
      connectedAccountId: string;
      name: string;
      city?: string;
      district?: string;
      coords?: string;
    }) => post<{ shop: { id: string } }>('/onboarding/shops', body),
  },

  insights: {
    summary: () =>
      get<
        DashboardSummary & {
          listedItems: number;
          outOfStockNow: number;
          shopNames: string[];
          hasSalesData: boolean;
          /** Statutory 2.5%, withheld from the proceeds rather than added. */
          taxWithheld30d: number;
          netRevenue30d: number;
          taxRate: number;
        }
      >('/insights/summary'),
    mispricing: () =>
      get<{ rows: MispricingRow[]; truncated: boolean; totalItems: number; tier: PlanTier }>(
        '/insights/mispricing',
      ),
    undercuts: () => get<{ findings: UndercutFinding[] }>('/insights/undercuts'),
    sales: (window: SalesWindow, side: TradeSide = 'sell') =>
      get<{
        sales: Array<{
          id: string;
          itemCode: string | null;
          displayName: string | null;
          side: 'buy' | 'sell';
          unitPrice: number;
          quantity: number;
          totalAmount: number;
          counterparty: string | null;
          occurredAt: string;
          shopName: string | null;
          rawType: string | null;
        }>;
        total: number;
        returned: number;
        from: string;
        to: string;
        range: SalesRangeKey;
        earliest: string | null;
        side: TradeSide;
        hasWebhook: boolean;
        totals: { revenue: number; units: number; transactions: number } | null;
        estimated: Array<{
          itemKey: string;
          material: string;
          displayName: string;
          unitsSold: number;
          estimatedRevenue: number;
          observations: number;
          restocks: number;
          firstSeen: string;
          lastSeen: string;
        }>;
        estimatedTotals: { units: number; revenue: number; items: number };
      }>(`/insights/sales${windowQuery(window, side)}`),
    myShop: () =>
      get<{
        rows: Array<{
          itemKey: string;
          material: string;
          displayName: string;
          isVariant: boolean;
          variantHash: string | null;
          variantLabel: string | null;
          variantVerified: boolean;
          variantConfirmations: number;
          variantEnchantments: Array<{ name: string; level: number }>;
          title: string;
          variantConfirmed: boolean;
          signs: Array<{
            shopId: number;
            shopGroupId: string | null;
            signPrice: number | null;
            batchQty: number;
            unitPrice: number;
            currentStock: number | null;
            stockAt: string | null;
            coords: string | null;
            world: string | null;
          }>;
          signPrice: number | null;
          batchQty: number;
          yourPrice: number;
          currentStock: number;
          stockAt: string | null;
          world: string | null;
          coords: string | null;
          shopGroupIds: Array<string | null>;
          marketPrice: number | null;
          marketShops: number;
          band: PriceBandShape | null;
          flaggedNa: boolean;
          deltaPct: number | null;
          unitsSold30d: number;
          revenue30d: number;
        }>;
        shops: Array<{
          id: string;
          name: string;
          world: string;
          coords: string;
          listingCount: number;
        }>;
        totalListings: number;
        totalSigns: number;
        outOfStock: number;
        variants: number;
        noMarket: number;
        unlabelledVariants: number;
        hasSalesData: boolean;
        quota: {
          tier: PlanTier;
          chestshopsUsed: number;
          locationsUsed: number;
          maxChestshops: number | null;
          maxLocations: number | null;
          overChestshops: number;
          overLocations: number;
          withinLimits: boolean;
          suggestedTier: PlanTier | null;
          hiddenSigns: number;
        };
        lastFetchedAt: string | null;
        medianUpstreamAgeHours: number | null;
      }>('/insights/my-shop'),
    salesSeries: (window: SalesWindow, side: TradeSide = 'sell') =>
      get<{
        source: 'webhook' | 'estimated' | 'none';
        side: TradeSide;
        granularity: 'hour' | 'day' | 'week' | 'month';
        from: string;
        to: string;
        range: SalesRangeKey;
        earliest: string | null;
        buckets: Array<{
          bucket: string;
          units: number;
          revenue: number;
          transactions: number;
        }>;
        totals: { units: number; revenue: number; transactions: number };
      }>(`/insights/sales/series${windowQuery(window, side)}`),
    refreshItem: (material: string) =>
      post<{ refreshed: boolean; listings: number; removed: number; reason: string }>(
        `/insights/my-shop/items/${encodeURIComponent(material)}/refresh`,
      ),
    hideItem: (itemKey: string) =>
      post<{ hidden: boolean; itemKey: string }>(
        `/insights/my-shop/items/${encodeURIComponent(itemKey)}/hide`,
      ),
    unhideAll: () => post<{ restored: number }>('/insights/my-shop/unhide-all'),
    // A flag belongs to one chestshop, not to the item. Omitting shopId is
    // still allowed for a shop owner flagging their own listing wholesale.
    flagItem: (itemKey: string, shopId?: number, reason?: string) =>
      post<{ flagged: boolean; note: string }>(
        `/insights/items/${encodeURIComponent(itemKey)}/flag`,
        { shopId, reason },
      ),
    clearFlag: (itemKey: string, shopId?: number) =>
      del<{ cleared: number }>(
        `/insights/items/${encodeURIComponent(itemKey)}/flag${shopId ? `?shopId=${shopId}` : ''}`,
      ),
    flagsFor: (itemKey: string) =>
      get<{ shopIds: number[] }>(`/insights/items/${encodeURIComponent(itemKey)}/flags`),
    labelVariant: (itemKey: string, text: string) =>
      post<{
        outcome: 'created' | 'confirmed';
        label: {
          hash: string;
          material: string;
          label: string;
          enchantments: Array<{ name: string; level: number }>;
          confirmations: number;
          confirmed: boolean;
          verified: boolean;
        };
      }>(`/insights/variants/${encodeURIComponent(itemKey)}/label`, { text }),
    variants: (material?: string) =>
      get<{
        labels: Array<{
          hash: string;
          material: string;
          label: string;
          enchantments: Array<{ name: string; level: number }>;
          confirmations: number;
          confirmed: boolean;
          verified: boolean;
        }>;
        coverage: { labelled: number; total: number };
      }>(`/insights/variants${material ? `?material=${encodeURIComponent(material)}` : ''}`),
    priceFor: (itemKey: string) =>
      get<{
        itemKey: string;
        material: string;
        band: PriceBandShape | null;
        shopsInStock: number;
        unitsAvailable: number;
      }>(`/insights/price/${encodeURIComponent(itemKey)}`),
    priceSearch: (q: string) =>
      get<{ items: Array<{ itemKey: string; material: string; shops: number }> }>(
        `/insights/price-search?q=${encodeURIComponent(q)}`,
      ),
    refreshShop: () =>
      post<{ itemsChecked: number; refreshed: number }>('/insights/my-shop/refresh'),
    demand: (days = 30) =>
      get<{ profile: DemandProfile; days: number; serverActivity: number[] }>(
        `/insights/demand?days=${days}`,
      ),
    restock: () => get<{ findings: RestockFinding[] }>('/insights/restock'),
    opportunities: (limit = 15) =>
      get<{ findings: OpportunityFinding[] }>(`/insights/opportunities?limit=${limit}`),
    performance: (days = 30, granularity: 'hour' | 'day' | 'week' = 'day') =>
      get<{
        shops: ShopPerformance[];
        days: number;
        granularity: string;
        portfolioAvailable: boolean;
        totalShops: number;
      }>(`/insights/performance?days=${days}&granularity=${granularity}`),
    recommendations: (body: { elasticity?: number } = {}) =>
      post<{ recommendations: PricingRecommendation[]; elasticityUsed: number; note: string }>(
        '/insights/recommendations',
        body,
      ),
  },

  market: {
    item: (itemKey: string, includeEmpty = false) =>
      get<{
        itemKey: string;
        material: string;
        band: PriceBandShape | null;
        suppressed: number;
        totals: { shops: number; units: number; cheapest: number | null };
        offers: Array<{
          shopId: number;
          ownerName: string | null;
          adminShop: boolean;
          signPrice: number | null;
          batchQty: number;
          unitPrice: number;
          currentStock: number | null;
          stockAt: string | null;
          world: string | null;
          coords: string | null;
        }>;
      }>(
        `/market/item/${encodeURIComponent(itemKey)}${includeEmpty ? '?includeEmpty=true' : ''}`,
      ),
    serverActivity: () =>
      get<{ curve: number[]; note: string | null; source: string }>('/market/server-activity'),
    /** Public: real items and bands for the landing page. */
    showcase: () =>
      get<{
        items: Array<{
          itemKey: string;
          material: string;
          shops: number;
          band: PriceBandShape;
        }>;
        note: string;
      }>('/market/showcase'),
    benchmarks: (items?: string[]) =>
      get<{ benchmarks: Array<Benchmark & { displayName: string }> }>(
        `/market/benchmarks${items?.length ? `?items=${items.join(',')}` : ''}`,
      ),
    stats: () =>
      get<{
        stats: {
          totalSales: number;
          totalVolume: string;
          distinctItems: number;
          activeShops: number;
          fetchedAt: string;
        } | null;
        source: string;
      }>('/market/stats'),
    history: (itemCode: string) =>
      get<{
        itemCode: string;
        displayName: string;
        source: 'chestshop_market' | 'network_only';
        points: Array<{
          day: string;
          median: number;
          samples: number;
          quantity: number;
          yours: number | null;
        }>;
      }>(`/market/benchmarks/${itemCode}/history`),
    whereToBuy: (q: string) =>
      get<{
        results: Array<{
          itemKey: string;
          material: string;
          itemName: string;
          bestUnitPrice: number;
          marketAvg: number | null;
          shopsInStock: number;
          totalStock: number;
          offers: Array<{
            shopId: number;
            ownerName: string | null;
            signPrice: number | null;
            batchQty: number;
            unitPrice: number;
            currentStock: number | null;
            world: string | null;
            coords: string | null;
            adminShop: boolean;
            savingVsAvgPct: number | null;
          }>;
        }>;
        note: string | null;
      }>(`/market/where-to-buy?q=${encodeURIComponent(q)}`),
    competitors: (itemCode: string) =>
      get<{
        itemCode: string;
        displayName: string;
        marketAvg: number | null;
        activeShopCount: number;
        competitors: Array<{
          shopId: number;
          ownerName: string | null;
          world: string | null;
          coords: string | null;
          unitPrice: number;
          batchQty: number;
          currentStock: number | null;
          stockAt: string | null;
          adminShop: boolean;
        }>;
      }>(`/market/items/${itemCode}/competitors`),
    baselines: () =>
      get<{
        observations: Array<{
          id: string;
          itemCode: string;
          displayName: string;
          averagePrice: number;
          storeCount: number | null;
          district: string | null;
          observedAt: string;
        }>;
      }>('/market/baseline'),
    addBaseline: (body: {
      itemCode: string;
      averagePrice: number;
      storeCount?: number;
      district?: string;
      note?: string;
    }) => post<{ observation: unknown }>('/market/baseline', body),
  },

  alerts: {
    list: (unreadOnly = false) =>
      get<{ alerts: Alert[]; unread: number }>(`/alerts?unreadOnly=${unreadOnly}`),
    markRead: (id: string) => post<{ ok: boolean }>(`/alerts/${id}/read`),
    markAllRead: () => post<{ updated: number }>('/alerts/read-all'),
    settings: () =>
      get<{
        settings: {
          undercutThresholdPct: number;
          mispricingThresholdPct: number;
          channels: string[];
        };
      }>('/alerts/settings'),
    updateSettings: (body: {
      undercutThresholdPct?: number;
      mispricingThresholdPct?: number;
      discordWebhookUrl?: string | null;
      channels?: string[];
    }) => patch<{ settings: unknown }>('/alerts/settings', body),
    evaluate: () => post<{ created: number; delivered: number }>('/alerts/evaluate'),
  },

  billing: {
    plans: () =>
      get<{
        plans: PlanDefinition[];
        addOns: {
          extraLocation: { price: number; unit: string };
          extraChestshops: { price: number; blockSize: number; unit: string };
        };
        promo: { tier: PlanTier; months: number; blurb: string };
      }>('/billing/plans'),
    usage: () =>
      get<{
        tier: PlanTier;
        chestshopsUsed: number;
        locationsUsed: number;
        maxChestshops: number | null;
        maxLocations: number | null;
        overChestshops: number;
        overLocations: number;
        withinLimits: boolean;
        suggestedTier: PlanTier | null;
      }>('/billing/usage'),
    claimPromo: () =>
      post<{ tier: PlanTier; until: string; message: string }>('/billing/promo', {}),
    subscription: () =>
      get<{
        subscription: (Subscription & { invoices: Invoice[] }) | null;
        plan: PlanDefinition;
        billingAccount: string;
      }>('/billing/subscription'),
    subscribe: (
      tier: PlanTier,
      addOns?: { extraChestshops?: number; extraLocations?: number },
    ) =>
      post<{
        invoice: Invoice | null;
        instructions: {
          amountFormatted: string;
          payTo: string;
          reference: string;
          steps: string[];
          note: string;
        } | null;
      }>('/billing/subscribe', { tier, ...addOns }),
    invoices: () => get<{ invoices: Invoice[]; billingAccount: string }>('/billing/invoices'),
  },

  jobs: {
    list: (kind?: 'na_check' | 'name_resolution') =>
      get<{
        jobs: Array<{
          id: string;
          kind: 'na_check' | 'name_resolution';
          itemKey: string;
          material: string;
          reward: string;
          shopId: number | null;
        }>;
        mine: {
          inProgress: Array<{ id: string; kind: string; itemKey: string; status: string }>;
          approved: number;
          rejected: number;
          balance: number;
        };
        rates: {
          nameResolution: number;
          naCheck: number;
          fakePenalty: number;
          rejectionsBeforePenalty: number;
        };
      }>(`/jobs${kind ? `?kind=${kind}` : ''}`),
    claim: (id: string) => post<{ job: unknown }>(`/jobs/${id}/claim`),
    submit: (id: string, submission: string) =>
      post<{ job: unknown }>(`/jobs/${id}/submit`, { submission }),
    review: (id: string, verdict: 'approve' | 'reject', note?: string) =>
      post<{ status: string; note: string }>(`/jobs/${id}/review`, { verdict, note }),
  },

  admin: {
    overview: () =>
      get<{
        subscribers: number;
        subscribersByTier: Record<string, number>;
        connections: number;
        verifiedConnections: number;
        transactionEvents: number;
        ingestedLast24h: number;
        deadLetters: number;
        unmatchedBillingEvents: number;
      }>('/admin/overview'),
    coverage: () =>
      get<{
        stats: CoverageStat[];
        summary: { good: number; thin: number; none: number };
        thresholds: { minSampleSize: number; minSellerCount: number };
      }>('/admin/coverage'),
    subscribers: () =>
      get<{
        subscribers: Array<{
          id: string;
          email: string;
          tier: PlanTier;
          role: AccountRole;
          isAdmin: boolean;
          status: string;
          createdAt: string;
          connections: number;
          verified: number;
          lastEventAt: string | null;
        }>;
      }>('/admin/subscribers'),
    setTier: (id: string, tier: PlanTier) =>
      post<{ email: string; tier: PlanTier }>(`/admin/subscribers/${id}/tier`, { tier }),
    flooders: () =>
      get<{
        flooders: Array<{
          ownerName: string;
          note: string | null;
          createdAt: string;
          suppressedSigns: number;
        }>;
      }>('/admin/flooders'),
    addFlooder: (ownerName: string, note?: string) =>
      post<{ listed: number; run: Record<string, number | string> }>('/admin/flooders', {
        ownerName,
        note,
      }),
    removeFlooder: (ownerName: string) =>
      del<{ released: number }>(`/admin/flooders/${encodeURIComponent(ownerName)}`),
    sweepFlooders: () =>
      post<{ runs: Array<Record<string, number | string>> }>('/admin/flooders/sweep', {}),
    subscriberDetail: (id: string) =>
      get<{
        subscriber: {
          id: string;
          email: string;
          tier: PlanTier;
          role: AccountRole;
          isAdmin: boolean;
          createdAt: string;
        };
        connections: Array<{
          id: string;
          label: string | null;
          dcAccountCode: string;
          accountType: string;
          ownerName: string | null;
          webhookStatus: string;
          eventCount: number;
          lastEventAt: string | null;
        }>;
        shops: Array<{
          shopId: number;
          itemKey: string;
          material: string;
          unitPrice: number | null;
          currentStock: number | null;
          world: string | null;
          coords: string | null;
          suppressed: boolean;
        }>;
        totals: { signs: number; items: number; stockValue: number };
      }>(`/admin/subscribers/${id}`),
    deadLetters: () =>
      get<{
        deadLetters: Array<{ id: string; reason: string; createdAt: string; payload: unknown }>;
      }>('/admin/dead-letters'),
    rebuildBenchmarks: () => post<{ written: number }>('/admin/jobs/rebuild-benchmarks'),
    evaluateAlerts: () => post<{ results: unknown[] }>('/admin/jobs/evaluate-alerts'),
    runBilling: () => post<{ issued: number; downgraded: number }>('/admin/jobs/billing'),
  },
};
